import React from 'react';
import {
  SkeletonBlock,
  SkeletonCircle,
  SkeletonNavItem,
} from './SkeletonBase';

/**
 * HubToolSkeleton - Skeleton for hub tool pages
 *
 * Matches the layout of pages with:
 * - Top navigation bar (56px) - full width
 * - SideNav sidebar (255px) - optional, sits below the top bar
 * - Main content area
 */

interface HubToolSkeletonProps {
  showSideNav?: boolean;
}

/**
 * Top navigation bar skeleton (full width, 56px tall)
 */
export const TopNavSkeleton = () => (
  <div className="fixed inset-x-0 top-0 h-14 z-50" style={{ backgroundColor: '#350944' }} />
);

/**
 * SideNav sidebar skeleton (255px wide)
 * Shows program navigation structure
 */
export const SideNavSkeleton = () => (
  <div
    className="hidden md:flex flex-col bg-gray-50 border-r border-gray-200 h-full"
    style={{ width: '255px' }}
  >
    {/* Header with logo */}
    <div className="p-4" style={{ backgroundColor: "#350944" }}>
      <div className="flex items-center gap-3">
        <SkeletonCircle size="2rem" className="opacity-30" />
        <SkeletonBlock height="1.25rem" width="60%" className="opacity-30" />
      </div>
    </div>

    {/* Navigation items */}
    <div className="flex-1 p-2 space-y-1">
      {/* Back button area */}
      <div className="py-3 border-b border-gray-200 mb-2">
        <SkeletonNavItem showIcon />
      </div>

      {/* Section header */}
      <SkeletonBlock height="0.75rem" width="40%" className="mb-3 ml-3" />

      {/* Nav items */}
      {Array.from({ length: 6 }).map((_, i) => (
        <SkeletonNavItem key={i} showIcon={false} />
      ))}

      {/* Second section */}
      <div className="pt-4 mt-4 border-t border-gray-200">
        <SkeletonBlock height="0.75rem" width="50%" className="mb-3 ml-3" />
        {Array.from({ length: 3 }).map((_, i) => (
          <SkeletonNavItem key={`s2-${i}`} showIcon={false} />
        ))}
      </div>
    </div>
  </div>
);

/**
 * Main content area skeleton for hub tool pages (Programs index)
 * Matches: Header banner → ContentContainer → section title → 3-col card grid
 * Cards mirror the actual ProgramUnitLinkCard: title at top, link at bottom, min-h-[175px]
 */
export const HubContentSkeleton = () => (
  <div className="flex-1 bg-muted min-h-screen">
    {/* Header banner area — matches the <Header> component with headerImage */}
    <div
      className="w-full flex items-end px-6 pb-8"
      style={{ backgroundColor: '#350944', minHeight: '140px' }}
    >
      <SkeletonBlock height="2rem" width="35%" style={{ opacity: 0.25 }} />
    </div>

    {/* ContentContainer — max-w-[1160px] mx-auto px-6 py-8 */}
    <div className="max-w-[1160px] mx-auto px-6 py-8 space-y-6">
      {/* Section title */}
      <SkeletonBlock height="1.5rem" width="180px" className="mb-2" />

      {/* Program card grid — 3 columns matching the actual layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className="bg-white rounded-lg border border-gray-200 p-4 flex flex-col justify-between gap-2"
            style={{ minHeight: '175px' }}
          >
            {/* Title placeholder */}
            <SkeletonBlock height="1.25rem" width="75%" />
            {/* "Go to Resource" link placeholder */}
            <SkeletonBlock height="1rem" width="40%" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

/**
 * Complete HubTool page skeleton
 * Combines the top navigation bar, SideNav, and content area
 */
export const HubToolSkeleton = ({
  showSideNav = true,
}: HubToolSkeletonProps) => (
  <div className="flex h-screen w-full overflow-hidden">
    <TopNavSkeleton />

    {showSideNav && (
      <div className="fixed top-14 h-[calc(100%-3.5rem)] left-0 z-40">
        <SideNavSkeleton />
      </div>
    )}

    <div
      className="flex-1 pt-14"
      style={{ marginLeft: showSideNav ? '255px' : '0' }}
    >
      <HubContentSkeleton />
    </div>
  </div>
);

export default HubToolSkeleton;
